import { useEffect, useMemo, useRef, useState } from 'react'
import * as MovHttp from '../../../../../services/api/https/patio/balanca-movimentacao.http'
import { errorHandler } from '../../../../../services/util/error.service'
import { parseToMysqlFormat } from '../../../../../services/util/date.service'

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  SAVE: 'Salvando informações da movimentação',
  SUCCESS: 'Salvo com sucesso',
}

const MANDATORY_FIELDS = {
  BALANCA: 'Selecione a Balança',
  DATA_ENTRADA: 'Data de entrada deve ser preenchida',
  HORA_ENTRADA: 'Hora de entrada deve ser preenchida',
  PESO_ENTRADA: 'Peso de entrada deve ser preenchido',
  PLACA: 'Placa deve ser preenchida',
  MOTORISTA: 'Motorista deve ser preenchido',
}

const INITIAL = {
  id: undefined,
  patio_balanca_id: null,
  _dataEntrada: new Date(),
  _horaEntrada: '',
  peso_entrada: '',
  _dataSaida: null,
  _horaSaida: '',
  peso_saida: '',
  placa: '',
  motorista: '',
  rg: '',
  tipo_carga: '',
  observacao: '',
  status: undefined,
}

const parseDateOnly = s => {
  if (!s) return null
  const d = new Date(s)
  if (!isNaN(d.getTime())) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate())
  }
  const str = String(s).split('T')[0]
  const parts = str.split('-').map(Number)
  if (parts.length === 3 && parts.every(n => !Number.isNaN(n))) {
    return new Date(parts[0], parts[1] - 1, parts[2])
  }
  return null
}

export default function useMovimentacaoModal() {
  const [state, setState] = useState({
    mov: { ...INITIAL },
    balancas: [],
    loadingMessage: LOAD_MESSAGE.NONE,
    errorMessage: null,
    instanceKey: 0,
  })

  const stateRef = useRef(state)
  useEffect(() => {
    stateRef.current = state
  }, [state])

  const localDispatch = useMemo(() => {
    return {
      async start(mov, selectedTp, balancasFromParent = []) {
        try {
          const balancas = Array.isArray(balancasFromParent) ? balancasFromParent : []

          if (mov) {
            const m = {
              ...INITIAL,
              ...mov,
              patio_balanca_id: mov.patio_balanca_id,
              _dataEntrada: parseDateOnly(mov.data_entrada) || new Date(),
              _horaEntrada: mov.hora_entrada ? mov.hora_entrada.substring(0, 5) : null,
              _dataSaida: parseDateOnly(mov.data_saida),
              _horaSaida: mov.hora_saida ? mov.hora_saida.substring(0, 5) : null,
              peso_entrada: mov.peso_entrada || '',
              peso_saida: mov.peso_saida || '',
              placa: mov.placa || '',
            }

            setState(prev => ({
              ...prev,
              balancas,
              mov: m,
              instanceKey: (prev.instanceKey || 0) + 1,
            }))
          } else {
            setState(prev => ({
              ...prev,
              balancas,
              mov: { ...INITIAL },
              instanceKey: (prev.instanceKey || 0) + 1,
            }))
          }
        } catch (err) {
          setState(prev => ({ ...prev, errorMessage: errorHandler(err) }))
        }
      },

      onChange(field, value) {
        setState(prev => ({ ...prev, mov: { ...prev.mov, [field]: value } }))
      },

      onBtnSalvarClicked(selectedTp) {
        const m = stateRef.current.mov
        if (!m.patio_balanca_id)
          return setState(prev => ({ ...prev, errorMessage: MANDATORY_FIELDS.BALANCA }))
        if (!m._dataEntrada)
          return setState(prev => ({ ...prev, errorMessage: MANDATORY_FIELDS.DATA_ENTRADA }))
        if (!m._horaEntrada || m._horaEntrada.length < 4)
          return setState(prev => ({ ...prev, errorMessage: MANDATORY_FIELDS.HORA_ENTRADA }))
        if (m.peso_entrada === '' || m.peso_entrada === null)
          return setState(prev => ({ ...prev, errorMessage: MANDATORY_FIELDS.PESO_ENTRADA }))
        if (!m.placa || m.placa.trim() === '')
          return setState(prev => ({ ...prev, errorMessage: MANDATORY_FIELDS.PLACA }))
        if (!m.motorista || m.motorista.trim() === '')
          return setState(prev => ({ ...prev, errorMessage: MANDATORY_FIELDS.MOTORISTA }))

        const toDateTime = (d, hhmm) => {
          if (!d || !hhmm) return null
          const [h, m] = hhmm.split(':').map(Number)
          const dt = new Date(d)
          dt.setHours(h || 0, m || 0, 0, 0)
          return dt
        }
        const mref = stateRef.current.mov
        const dtIn = toDateTime(mref._dataEntrada, mref._horaEntrada)
        const dtOut = toDateTime(mref._dataSaida, mref._horaSaida)
        if (dtIn && dtOut && dtIn.getTime() > dtOut.getTime()) {
          return setState(prev => ({
            ...prev,
            errorMessage: 'Data e Hora de Entrada deve ser anterior à Data e Hora de Saída',
          }))
        }

        setState(prev => ({ ...prev, errorMessage: null, loadingMessage: LOAD_MESSAGE.SAVE }))
      },

      async save(selectedTp) {
        try {
          const m = stateRef.current.mov
          const toNumberOrNull = v => (v === '' || v === null || v === undefined ? null : Number(v))

          const payload = {
            id: m.id,
            patio_balanca_id: m.patio_balanca_id,
            data_entrada: parseToMysqlFormat(m._dataEntrada),
            hora_entrada:
              (m._horaEntrada || '').length === 5 ? `${m._horaEntrada}:00` : m._horaEntrada || '',
            peso_entrada: toNumberOrNull(m.peso_entrada),

            data_saida: m._dataSaida ? parseToMysqlFormat(m._dataSaida) : null,
            hora_saida:
              m._dataSaida && m._horaSaida
                ? m._horaSaida.length === 5
                  ? `${m._horaSaida}:00`
                  : m._horaSaida
                : null,
            peso_saida: toNumberOrNull(m.peso_saida),

            placa: (m.placa || '').toUpperCase(),
            motorista: m.motorista,
            rg: m.rg || null,
            tipo_carga: m.tipo_carga || null,
            observacao: m.observacao || null,
          }

          const saved = await MovHttp.saveMovimentacao(payload)
          setState(prev => ({ ...prev, loadingMessage: LOAD_MESSAGE.SUCCESS }))
          return saved
        } catch (err) {
          setState(prev => ({
            ...prev,
            loadingMessage: LOAD_MESSAGE.NONE,
            errorMessage: errorHandler(err),
          }))
          throw err
        } finally {
          setState(prev => ({ ...prev, loadingMessage: LOAD_MESSAGE.NONE }))
        }
      },

      reset() {
        setState({
          mov: { ...INITIAL },
          balancas: [],
          loadingMessage: LOAD_MESSAGE.NONE,
          errorMessage: null,
          instanceKey: 0,
        })
      },
    }
  }, [])

  return { state, localDispatch }
}
